import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getMyCategoryQuizzes, type MyCategoryQuizzes, type CategoryQuiz, type CategoryTheme } from "../../services/quizzes";
import { resolveMediaUrl } from "../student/quiz/media";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameIcon, GameCard, GameHero, Skeleton } from "../../components/game";

const LEVELS: { key: "easy" | "medium" | "hard"; label: string; color: string; bg: string }[] = [
  { key: "easy", label: "Ușor", color: "#059669", bg: "bg-emerald-100 text-emerald-700" },
  { key: "medium", label: "Mediu", color: "#d97706", bg: "bg-amber-100 text-amber-700" },
  { key: "hard", label: "Greu", color: "#e11d48", bg: "bg-rose-100 text-rose-700" },
];

// URL segment for the bucket of quizzes that have no theme (theme id null).
const NO_THEME = "none";
const themeKey = (id: string | null | undefined) => id ?? NO_THEME;

export default function CategoryQuizzes() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // `themeId` is set on the per-theme screen (/category/:name/theme/:themeId).
  // Without it, a category that spans several themes shows the theme picker
  // first; a single-theme category goes straight to its quizzes as before.
  const { name, themeId } = useParams<{ name: string; themeId?: string }>();

  const [data, setData] = useState<MyCategoryQuizzes | null>(null);
  const [loading, setLoading] = useState(true);
  // Shown when a Free-plan athlete taps a quiz that isn't in the Free plan.
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    if (!name) return;
    setLoading(true);
    getMyCategoryQuizzes(decodeURIComponent(name))
      .then(setData)
      .catch((e) => console.error("Failed to load category quizzes:", e))
      .finally(() => setLoading(false));
  }, [name]);

  // Quizzes are rendered in the exact order the backend returns them
  // (theme_order asc, then created_at asc) so the admin's configured
  // sequence — e.g. a 1→9 learning path — is preserved. We deliberately do
  // NOT regroup by difficulty here: that split reordered a themed sequence
  // whenever its quizzes spanned difficulties (categories with a uniform
  // difficulty looked fine, mixed ones came out scrambled/reversed).
  const themes: CategoryTheme[] = data?.themes || [];
  const showThemePicker = !themeId && themes.length > 1;
  const currentTheme = themeId ? themes.find((th) => themeKey(th.id) === themeId) : undefined;
  // Filtering keeps the backend order, so the theme's sequence is untouched.
  const orderedQuizzes = (data?.quizzes || []).filter((q) => !themeId || themeKey(q.theme_id) === themeId);
  const levelMeta = (key: string) => LEVELS.find((l) => l.key === key);

  const totalCount = orderedQuizzes.length;
  const completedCount = orderedQuizzes.filter((q) => q.status === "completed").length;

  const categoryPath = `/app/quizzes/category/${encodeURIComponent(name || "")}`;
  const themeTitle = (th: CategoryTheme | undefined) =>
    th?.title || t("categoryQuizzes.otherQuizzes", "Alte quiz-uri");
  // Header: on a theme screen the theme is the title and the category becomes
  // the back target; otherwise the category is the title, back to the grid.
  const headerTitle = themeId ? (data ? themeTitle(currentTheme) : null) : data?.category.title;
  const backTo = themeId ? categoryPath : "/app/quizzes";
  const backLabel = themeId ? data?.category.title || t("categoryQuizzes.back", "Categorii") : t("categoryQuizzes.back", "Categorii");

  const statusChip = (q: CategoryQuiz) => {
    if (q.plan_locked)
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold">
          <GameIcon name="lock" className="w-3 h-3" />
          {t("categoryQuizzes.premiumOnly", "Doar cu abonament")}
        </span>
      );
    if (q.locked)
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-500 text-[11px] font-bold">
          <GameIcon name="lock" className="w-3 h-3" />
          {t("categoryQuizzes.locked", "Blocat")}
        </span>
      );
    if (q.status === "completed")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold">
          <GameIcon name="check" className="w-3 h-3 text-emerald-600" />
          {t("categoryQuizzes.completed", "Finalizat")}{q.best_score != null && ` · ${q.best_score}%`}
        </span>
      );
    if (q.status === "in_progress")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold">
          <GameIcon name="clock" className="w-3 h-3" />
          {t("categoryQuizzes.inProgress", "Început")}
        </span>
      );
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold">
        {t("categoryQuizzes.notStarted", "Neînceput")}
      </span>
    );
  };

  const actionLabel = (q: CategoryQuiz) =>
    q.status === "completed"
      ? t("categoryQuizzes.redo", "Refă")
      : q.status === "in_progress"
      ? t("categoryQuizzes.continue", "Continuă")
      : t("categoryQuizzes.start", "Începe");

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-5">
        {/* Header */}
        <GameHero flow className="-mx-4 -mt-6 rounded-br-3xl px-4 pt-3 pb-4">
          <button onClick={() => navigate(backTo)} className="flex items-center gap-1.5 text-white/80 text-sm font-semibold mb-2 max-w-full">
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            <span className="truncate">{backLabel}</span>
          </button>
          <div className="flex items-center gap-3">
            {data?.category.image_url && (
              <img src={resolveMediaUrl(data.category.image_url) || undefined} alt="" className="w-11 h-11 rounded-xl object-cover shrink-0 ring-2 ring-white/30" />
            )}
            <div className="min-w-0">
              {/* Show the real category title only once loaded — never the raw
                  URL slug (e.g. "educatie_sportiva"), which used to flash for a
                  moment while the request was in flight. */}
              {headerTitle ? (
                <h1 className="text-xl font-extrabold truncate">{headerTitle}</h1>
              ) : (
                <div className="h-6 w-40 max-w-full rounded-lg bg-white/20 animate-pulse" />
              )}
              {showThemePicker ? (
                <p className="text-white/70 text-[13px] mt-0.5">
                  {themes.length} {t("categoryQuizzes.themesCount", "teme")} · {t("categoryQuizzes.pickTheme", "alege o temă")}
                </p>
              ) : totalCount > 0 && (
                <p className="text-white/70 text-[13px] mt-0.5 tabular-nums">
                  {completedCount}/{totalCount} {t("categoryQuizzes.done", "finalizate")}
                </p>
              )}
            </div>
          </div>
        </GameHero>

        {loading ? (
          <div className="space-y-4 animate-fadeIn">
            <Skeleton className="h-6 w-24 rounded-lg" />
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-20 rounded-2xl" />
          </div>
        ) : showThemePicker ? (
          /* Intermediate screen: the category spans several themes, so list
             the themes first; tapping one lists its quizzes in order. */
          <div className="space-y-2.5">
            {themes.map((th, idx) => {
              const done = th.total > 0 && th.completed >= th.total;
              return (
                <GameCard
                  key={themeKey(th.id)}
                  onClick={() => navigate(`${categoryPath}/theme/${encodeURIComponent(themeKey(th.id))}`)}
                  className="p-4 cursor-pointer active:translate-y-[2px] transition-transform"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-xl grid place-items-center text-white font-extrabold shadow-[0_2px_0_rgba(0,0,0,0.15)] ${
                        done ? "bg-gradient-to-br from-emerald-400 to-emerald-600" : "bg-gradient-to-br from-indigo-500 to-blue-600"
                      }`}
                    >
                      {done ? <GameIcon name="check" className="w-5 h-5 text-white" /> : th.id ? idx + 1 : <GameIcon name="bulb" className="w-5 h-5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 truncate">{themeTitle(th)}</p>
                      <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <GameIcon name="bulb" className="w-3.5 h-3.5" />
                          {th.total} {th.total === 1 ? t("quizzes.quizOne", "quiz") : t("quizzes.quizMany", "quiz-uri")}
                        </span>
                        <span className="tabular-nums font-semibold">
                          {th.completed}/{th.total} {t("categoryQuizzes.done", "finalizate")}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${done ? "bg-emerald-500" : "bg-indigo-500"}`}
                          style={{ width: `${Math.max(th.percent, th.percent > 0 ? 6 : 0)}%` }}
                        />
                      </div>
                    </div>
                    <svg className="w-4 h-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </div>
                </GameCard>
              );
            })}
          </div>
        ) : totalCount === 0 ? (
          <GameCard className="p-8 text-center">
            <GameIcon name="bulb" className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-slate-500">{t("categoryQuizzes.empty", "Niciun quiz în această categorie încă")}</p>
          </GameCard>
        ) : (
          <div className="space-y-2.5">
            {orderedQuizzes.map((q) => {
                // A quiz with no questions can't be taken — don't open it.
                const empty = q.question_count === 0;
                const planLocked = !!q.plan_locked;
                const anyLocked = q.locked || planLocked;
                const blocked = anyLocked || empty;
                const lvl = levelMeta(q.difficulty);
                return (
                <GameCard
                  key={q.id}
                  onClick={
                    // Plan-locked: frozen but tappable → show the upgrade modal.
                    // Theme-locked / empty: not tappable. Otherwise: open the quiz.
                    planLocked
                      ? () => setUpgradeOpen(true)
                      : blocked
                      ? undefined
                      : () => navigate(`/quiz/${q.id}`, { state: { from: location.pathname } })
                  }
                  className={
                    planLocked
                      ? "p-4 opacity-70 cursor-pointer"
                      : blocked
                      ? "p-4 opacity-60"
                      : "p-4 cursor-pointer active:translate-y-[2px] transition-transform"
                  }
                  aria-disabled={(blocked && !planLocked) || undefined}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-xl grid place-items-center shadow-[0_2px_0_rgba(0,0,0,0.15)] ${
                        planLocked
                          ? "bg-gradient-to-br from-amber-400 to-amber-500"
                          : q.locked
                          ? "bg-gradient-to-br from-slate-300 to-slate-400"
                          : q.status === "completed"
                          ? "bg-gradient-to-br from-emerald-400 to-emerald-600"
                          : "bg-gradient-to-br from-indigo-500 to-blue-600"
                      }`}
                    >
                      <GameIcon name={anyLocked ? "lock" : q.status === "completed" ? "check" : "bulb"} className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 truncate">{q.title}</p>
                      <div className="flex items-center gap-2.5 mt-1 text-xs text-slate-500">
                        {lvl && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${lvl.bg}`}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: lvl.color }} />
                            {t(`quiz.${lvl.key}`, lvl.label)}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <GameIcon name="bulb" className="w-3.5 h-3.5" />
                          {q.question_count} {t("quiz.questions", "întrebări")}
                        </span>
                        <span className="inline-flex items-center gap-1 text-amber-600 font-bold">
                          <GameIcon name="star" className="w-3.5 h-3.5" />
                          {t("quiz.up_to", "până la")} +{q.max_points ?? q.reward_points}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-2">
                    {statusChip(q)}
                    {/* Theme progression takes priority over the plan hint: the
                        "Doar cu abonament" pill already conveys the plan lock,
                        so next to it we show the sequential prerequisite (like a
                        free quiz would). Plan-locked-only quizzes need no extra
                        text — the pill says it all. */}
                    {q.locked ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 text-right min-w-0">
                        <GameIcon name="lock" className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">
                          {q.prerequisite_title
                            ? t("categoryQuizzes.finishFirst", "Finalizează „{{title}}” întâi", { title: q.prerequisite_title })
                            : t("categoryQuizzes.lockedHint", "Finalizează quiz-ul anterior")}
                        </span>
                      </span>
                    ) : planLocked ? null : empty ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                        <GameIcon name="bulb" className="w-3.5 h-3.5 shrink-0" />
                        {t("categoryQuizzes.noQuestions", "Fără întrebări")}
                      </span>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-extrabold ${
                          q.status === "completed" ? "bg-slate-100 text-slate-700" : "bg-indigo-600 text-white"
                        }`}
                      >
                        {actionLabel(q)}
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                      </span>
                    )}
                  </div>
                </GameCard>
                );
              })}
          </div>
        )}
      </div>

      {/* Upgrade modal — a Free-plan athlete tapped a locked quiz. */}
      {upgradeOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-4"
          onClick={() => setUpgradeOpen(false)}
        >
          <div
            className="bg-white w-full max-w-sm rounded-3xl p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 grid place-items-center shadow-[0_4px_0_#b45309]">
              <GameIcon name="lock" className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-lg font-extrabold text-slate-800">
              {t("categoryQuizzes.upgradeTitle", "Quiz blocat")}
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              {t(
                "categoryQuizzes.upgradeBody",
                "Acest quiz este disponibil cu un abonament superior. Upgrade-ul se face din contul părintelui sau tutorelui tău.",
              )}
            </p>
            <button
              onClick={() => { setUpgradeOpen(false); navigate("/app/plans"); }}
              className="w-full mt-5 py-3 rounded-2xl bg-gradient-to-b from-indigo-500 to-blue-600 text-white font-extrabold text-sm shadow-[0_4px_0_#3730a3] active:translate-y-[3px] transition-transform"
            >
              {t("categoryQuizzes.upgradeCta", "Vezi planurile")}
            </button>
            <button
              onClick={() => setUpgradeOpen(false)}
              className="w-full mt-2 py-2.5 text-sm font-semibold text-slate-500"
            >
              {t("common.close", "Închide")}
            </button>
          </div>
        </div>
      )}
    </MobileAppLayout>
  );
}
